<script setup lang="ts" generic="T">
/**
 * A plain read-only table for a handful of rows inside a form or a section —
 * a payment schedule, the tax summary of a document: a head row, the rows and
 * an optional total row, nothing else. No settings, no selection, no cursor,
 * no scrolling, no paging; a row shows what it holds and does nothing. For
 * data a user works through (sorting, filtering, editing, many rows) use
 * `NxTable`.
 *
 * `columns` names the columns. A cell shows its row's field named by the
 * column's `id`, or the slot `cell-<id>` for rich content (a figure, a muted
 * second line). `align: 'end'` sets a column's head and cells on the right
 * edge (figures). With a `grow` column, that column takes the free width and
 * every other one shrinks to its content; without one the table shares its
 * width out over all columns.
 *
 * `totalRows` render in the table's foot as its one emphasis: semibold, set
 * apart by a strong rule. `hideHeader` hides the head row from sight only —
 * screen readers still name every cell by its column. `flush` lets the table
 * hang out by its cell padding, so the text of its first and last column
 * stands on the container's edges like the content above it. `dense`
 * tightens the rows, for cells of two lines.
 */
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    columns: readonly NxPlainTableColumn[];
    rows: readonly T[];
    /** A stable key per row (body and foot alike). */
    rowKey: (row: T) => string | number;
    /** Rows in the table's foot: the total, semibold under a strong rule. */
    totalRows?: readonly T[];
    /** Hide the head row from sight; it stays for screen readers. */
    hideHeader?: boolean;
    /** The text of the first and last column stands on the container's edges. */
    flush?: boolean;
    /** Tighter rows, for cells of two lines. */
    dense?: boolean;
  }>(),
  { totalRows: () => [], hideHeader: false, flush: false, dense: false }
);

defineSlots<{
  [cell: `cell-${string}`]: (props: {
    row: T;
    column: NxPlainTableColumn;
    /** The row is one of `totalRows`. */
    total: boolean;
  }) => unknown;
}>();

const hasGrow = computed(() => props.columns.some((column) => column.grow));

const cellClass = (column: NxPlainTableColumn) => ({
  'align-end': column.align === 'end',
  'is-grow': column.grow === true,
});

function fieldText(row: T, column: NxPlainTableColumn): string {
  const value = (row as Record<string, unknown>)[column.id];
  return value === null || value === undefined ? '' : String(value);
}
</script>

<script lang="ts">
export interface NxPlainTableColumn {
  /** The column's key: the row field a cell shows, and its slot `cell-<id>`. */
  id: string;
  /** The head text; it names the column for screen readers too. */
  label: string;
  /** `end` sets the head and the cells on the right edge (figures). */
  align?: 'start' | 'end';
  /** The column takes the free width; the other columns shrink to their content. */
  grow?: boolean;
}
</script>

<template>
  <table
    class="nx-plain-table"
    :class="{
      'is-flush': props.flush,
      'is-dense': props.dense,
      'has-grow': hasGrow,
      'is-headless': props.hideHeader,
    }"
  >
    <thead>
      <tr>
        <th v-for="column in props.columns" :key="column.id" scope="col" :class="cellClass(column)">
          {{ column.label }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in props.rows" :key="props.rowKey(row)">
        <td v-for="column in props.columns" :key="column.id" :class="cellClass(column)">
          <slot :name="`cell-${column.id}`" :row="row" :column="column" :total="false">
            {{ fieldText(row, column) }}
          </slot>
        </td>
      </tr>
    </tbody>
    <tfoot v-if="props.totalRows.length">
      <tr v-for="row in props.totalRows" :key="props.rowKey(row)">
        <td v-for="column in props.columns" :key="column.id" :class="cellClass(column)">
          <slot :name="`cell-${column.id}`" :row="row" :column="column" :total="true">
            {{ fieldText(row, column) }}
          </slot>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<style scoped>
.nx-plain-table {
  --plain-table-inset: var(--nxt-space-xs);
  /* A one-line row is as tall as a large control, so the rows run in the
     rhythm of the fields and sum rows beside the table; a cell with a second
     line grows the row, its first line stays on the others' baseline. */
  --plain-table-row-padding: calc((var(--nxt-control-size-lg) - 1lh - var(--nxt-hairline)) / 2);

  width: 100%;
  border-collapse: collapse;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-tight);
}

/* The table hangs out by its cells' inline padding: the text of its first and
   last column stands on the container's edges. */
.nx-plain-table.is-flush {
  width: calc(100% + 2 * var(--plain-table-inset));
  margin-inline: calc(-1 * var(--plain-table-inset));
}

.nx-plain-table.is-dense {
  --plain-table-row-padding: var(--nxt-space-xs);
}

th,
td {
  padding: var(--plain-table-row-padding) var(--plain-table-inset);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
  text-align: start;
  /* The first lines of a row share one baseline, whatever their size and
     line height: a figure beside its unit, a date over a muted note. */
  vertical-align: baseline;
}

th {
  padding-block: var(--nxt-space-2xs);
  background: var(--nxt-surface-sunken-bg);
  font-weight: var(--nxt-font-weight-semibold);
}

td {
  font-weight: var(--nxt-font-weight-regular);
}

.align-end {
  text-align: end;
}

/* One column takes the free width, the others keep to their content. */
.nx-plain-table.has-grow :is(th, td):not(.is-grow) {
  width: 0;
  white-space: nowrap;
}

/* The head row out of sight, still there for screen readers. */
.nx-plain-table.is-headless th {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

tbody tr:last-child td {
  border-bottom: none;
}

/* The one emphasis of the table: what it comes to. */
tfoot td {
  border-bottom: none;
  font-weight: var(--nxt-font-weight-semibold);
}

tfoot tr:first-child td {
  border-top: var(--nxt-hairline) solid var(--nxt-border-strong);
}
</style>
